import { HttpClient, HttpEvent, HttpHandler, HttpHeaders, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../environments/environment';

/**
 * Session, authentification et SSO. Remplace les paquets @ff/user, @ff/auth,
 * @ff/oauth2 et l'intercepteur de @ff/auth, qui etaient telecharges depuis
 * packagist.fastnflex.fr (registre hors service). Le code n'est pas nouveau :
 * c'est celui de ces paquets, recopie ici parce qu'il n'est plus installable.
 *
 * ATTENTION A L'ORDRE DE DECLARATION. En ViewEngine (enableIvy: false dans
 * tsconfig.json) les decorateurs @Injectable() sont evalues a la volee, dans
 * l'ordre du fichier : une classe injectee doit etre declaree avant celle qui
 * l'injecte, sinon son token de DI vaut undefined au moment de l'evaluation.
 * MpsUserService doit donc rester avant MpsAuthService.
 *
 * Les chemins doivent rester alignes sur les routes de l'API Laravel
 * (routes/api.php) : /user, /user/password, /user/password/reset,
 * /user/password/email, /auth/login, /auth/logout, /oauth2/providers,
 * /oauth2/token/{id}.
 */

// ---------------------------------------------------------------------------
// Utilisateur connecte (@ff/user)
// ---------------------------------------------------------------------------

/**
 * A ne pas confondre avec src/app/services/user.service.ts, qui etend
 * CrudService et sert de client REST d'administration : les deux classes
 * s'appelaient UserService, c'est le prefixe Mps qui leve l'ambiguite.
 */
@Injectable()
export class MpsUserService {

    public _user: BehaviorSubject<any> = new BehaviorSubject(null);

    public constructor(private _httpClient: HttpClient) {
    }

    public getUser(): BehaviorSubject<any> {
        return this._user;
    }

    public fetch(): Promise<any> {

        return this._httpClient.get<any>(environment.api.host + '/user', {})
            .toPromise()
            .then(response => (response.data))
            .then((user: any) => {
                this._user.next(user);

                return user;
            })
            .catch(() => {
                this._user.next(null);

                return null;
            });
    }

    public save(form: FormGroup): Promise<any> {

        const url: string = environment.api.host + '/user';

        const hasId: boolean = form.get('id') && form.get('id').value;

        return hasId ?
            this._httpClient.put(url, form.getRawValue()).toPromise() :
            this._httpClient.post(url, form.getRawValue()).toPromise();
    }

    public changePassword(form: FormGroup): Promise<any> {

        return this._httpClient.post(environment.api.host + '/user/password', form.getRawValue())
            .toPromise();
    }

    public resetPasswordLink(data: FormGroup): Promise<any> {

        return this._httpClient.post(environment.api.host + '/user/password/email', {...data.value})
            .toPromise();
    }

    public resetPassword(form: FormGroup): Promise<any> {

        return this._httpClient.post(environment.api.host + '/user/password/reset', form.getRawValue())
            .toPromise();
    }
}

// ---------------------------------------------------------------------------
// Etat connecte (@ff/auth)
// ---------------------------------------------------------------------------

/**
 * L'etat connecte n'est pas stocke ici : il est derive de MpsUserService par
 * abonnement a son BehaviorSubject (`user != null`). C'est pour cela que
 * logout() pousse `null` dans MpsUserService avant meme l'appel HTTP, et que
 * login() se termine par userService.fetch().
 *
 * Le jeton est ecrit dans localStorage sous la cle `_token`, relue par
 * MpsTokenInterceptor et par mpsFetchBearer : ne pas la renommer d'un seul cote.
 */
@Injectable()
export class MpsAuthService {

    public _isLoggedIn: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    public constructor(
        private _userService: MpsUserService,
        private _httpClient: HttpClient
    ) {
        this._userService.getUser().subscribe(
            (user: any) => {
                this._isLoggedIn.next(user != null);
            }
        );
    }

    public isLoggedIn(): BehaviorSubject<boolean> {
        return this._isLoggedIn;
    }

    public login(data: FormGroup): Promise<any> {

        return this._httpClient.post<any>(environment.api.host + '/auth/login', data.value)
            .toPromise()
            .then(response => (response.data))
            .then(response => {

                if (response.token) {
                    localStorage.setItem('_token', response.token);
                }

                return this._userService.fetch();
            });
    }

    public logout(): Promise<any> {

        this._userService.getUser().next(null);

        return this._httpClient.get(environment.api.host + '/auth/logout')
            .toPromise()
            .then(
                () => {
                    localStorage.removeItem('_token');
                }
            );
    }
}

// ---------------------------------------------------------------------------
// SSO (@ff/oauth2)
// ---------------------------------------------------------------------------

/**
 * Deroulement du SSO cote front :
 *  1. fetchProviders() interroge GET /oauth2/providers et alimente la liste
 *     affichee par le composant de login.
 *  2. login(url) appelle l'url du fournisseur puis redirige le navigateur.
 *  3. au retour, l'API redirige vers le front avec `?_et=<id>` ; fetchToken(id)
 *     echange cet id contre le JWT, qui est range sous `_token`. L'enchainement
 *     est fait par mpsFetchBearer, plus bas.
 *
 * Trois membres du paquet d'origine ne sont pas repris : fetchBearer() n'etait
 * appele par personne, et getState() / clearState() ne servaient qu'a lui. Le
 * `state` ecrit dans sessionStorage par login() n'etait donc relu nulle part,
 * il n'est plus ecrit.
 */
@Injectable()
export class MpsOauth2Service {

    private _providers: BehaviorSubject<any[]> = new BehaviorSubject([]);

    public constructor(private _httpClient: HttpClient) {
    }

    public getProviders(): BehaviorSubject<any> {
        return this._providers;
    }

    public login(url: string): Promise<any> {

        return this._httpClient.get<any>(url)
            .toPromise()
            .then(
                (response: any) => {
                    window.location.href = response.data.url;
                }
            );
    }

    public fetchProviders(): Promise<any> {

        return this._httpClient.get<any>(environment.api.host + '/oauth2/providers')
            .toPromise()
            .then(
                (response: any) => {
                    this._providers.next(response.data);
                }
            )
            .catch(() => { });
    }

    public fetchToken(id: string): Promise<any> {

        return this._httpClient.get<any>(environment.api.host + '/oauth2/token/' + id)
            .toPromise()
            .then((response: any) => response.data._token)
            .catch(() => { });
    }
}

// ---------------------------------------------------------------------------
// Intercepteur HTTP (@ff/auth)
// ---------------------------------------------------------------------------

/**
 * Equivalent de src/mps/interceptors/mps-token.interceptor.ts dans resi-front,
 * ecrit en classe et non en fonction parce qu'Angular 12 n'a pas encore les
 * intercepteurs fonctionnels.
 *
 * C'est lui qui ajoute `Authorization: Bearer <jwt>` et `Accept:
 * application/json` a toutes les requetes sortantes, en lisant le jeton dans
 * localStorage sous la cle `_token`. Rien d'autre dans l'application ne pose
 * cet en-tete (CrudService utilise HttpClient nu) : sans lui, toutes les
 * requetes authentifiees vers l'API repondraient 401.
 */
@Injectable()
export class MpsTokenInterceptor implements HttpInterceptor {

    public intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

        const token = localStorage.getItem('_token');

        let headers: HttpHeaders = new HttpHeaders();

        if (req.headers) {
            headers = req.headers;
        }

        if (token) {
            headers = headers
                .set('Accept', `application/json`)
                .set('Authorization', `Bearer ${token}`);
        }

        const duplicate = req.clone({ headers });

        return next.handle(duplicate);
    }
}

// ---------------------------------------------------------------------------
// Initialiseurs (APP_INITIALIZER)
// ---------------------------------------------------------------------------

/**
 * Branchee dans app/modules/core/core.module.ts : Angular attend cette promesse
 * avant de demarrer, donc l'utilisateur est deja connu au premier rendu et les
 * gardes prennent la bonne decision d'entree. La retirer ferait demarrer
 * l'application en etat deconnecte.
 */
export function mpsFetchUser(userService: MpsUserService) {
    return () => userService.fetch();
}

/**
 * Branchee dans app/modules/auth/auth.module.ts. C'est le point d'atterrissage
 * du retour de SSO : elle doit rester un initialiseur et non un traitement dans
 * le composant de login, parce que l'API peut renvoyer le `_et` sur n'importe
 * quelle route du front (config/oauth2.php : redirectAfterLogin vaut '/' par
 * defaut).
 *
 * Comportement d'origine conserve : la promesse de fetchProviders() n'est pas
 * attendue, seule celle de fetchToken l'est. La liste des fournisseurs peut
 * donc arriver apres le premier rendu, c'est pour cela que le composant de
 * login s'y abonne au lieu de la lire une fois.
 */
export function mpsFetchBearer(oauth2Service: MpsOauth2Service, userService: MpsUserService) {
    return () => {

        let promise = null;

        oauth2Service.fetchProviders();

        const url = new URL(window.location.href);
        const id = url.searchParams.get('_et');

        if (id) {
            promise = oauth2Service.fetchToken(id).then((token) => {
                localStorage.setItem('_token', token);

                return userService.fetch();
            });
        }

        return promise;
    };
}
